import type { CanvaDesignConfig } from './serviceConfigurations'

type CanvaDimensions = CanvaDesignConfig['dimensions']

/**
 * Aspect ratios offered when starting a Canva design. The named ratios drive
 * editor autocomplete for the shared {@link CanvaRatioOptions} list, while the
 * `${string}:${string}` member also admits the synthetic "<width>:<height>"
 * ratio that the channel-default item builds for off-list channel sizes.
 */
export type CanvaRatio =
  | '1:1'
  | '4:5'
  | '3:4'
  | '1.91:1'
  | '9:16'
  | `${string}:${string}`

export type CanvaRatioOption = {
  id: string
  label: string
  ratio: CanvaRatio
  dimensions: CanvaDimensions
}

export type CanvaRatioMenuItem = CanvaRatioOption & {
  isDefault: boolean
}

/**
 * Shared aspect ratio options offered for every channel when starting
 * a new Canva design. The channel's own default (from
 * CanvaServiceConfigurations) is surfaced via getCanvaRatioMenuItems.
 */
export const CanvaRatioOptions: CanvaRatioOption[] = [
  {
    id: 'square',
    label: 'Square',
    ratio: '1:1',
    dimensions: { width: '1080', height: '1080', units: 'px' },
  },
  {
    id: 'portrait-4-5',
    label: 'Portrait',
    ratio: '4:5',
    dimensions: { width: '1080', height: '1350', units: 'px' },
  },
  {
    id: 'portrait-3-4',
    label: 'Portrait',
    ratio: '3:4',
    dimensions: { width: '1080', height: '1440', units: 'px' },
  },
  {
    id: 'landscape',
    label: 'Landscape',
    ratio: '1.91:1',
    dimensions: { width: '1200', height: '627', units: 'px' },
  },
  {
    id: 'story',
    label: 'Story',
    ratio: '9:16',
    dimensions: { width: '1080', height: '1920', units: 'px' },
  },
]

function greatestCommonDivisor(a: number, b: number): number {
  return b === 0 ? a : greatestCommonDivisor(b, a % b)
}

// Above this, a GCD-reduced term (ex. 235:197) is too awkward to show, so we
// fall back to a decimal ratio normalised to ":1" (ex. 1.19:1).
const MAX_CLEAN_RATIO_TERM = 20

/**
 * Reduces pixel dimensions to their simplest aspect ratio (ex. 1200×1200 →
 * "1:1"). Ratios that don't reduce cleanly are rounded to a decimal form
 * (ex. 940×788 → "1.19:1") instead of an unhelpful "235:197".
 */
function getReducedAspectRatio(width: string, height: string): CanvaRatio {
  const w = Number(width)
  const h = Number(height)
  const divisor = greatestCommonDivisor(w, h) || 1
  const reducedWidth = w / divisor
  const reducedHeight = h / divisor

  if (
    reducedWidth <= MAX_CLEAN_RATIO_TERM &&
    reducedHeight <= MAX_CLEAN_RATIO_TERM
  ) {
    return `${reducedWidth}:${reducedHeight}`
  }

  return w >= h
    ? `${Number((w / h).toFixed(2))}:1`
    : `1:${Number((h / w).toFixed(2))}`
}

/**
 * Builds the ratio menu for a channel: the channel default first
 * (marked as default), followed by the remaining shared options.
 *
 * The default is matched against the shared list first by exact pixels, then
 * by aspect ratio — a ratio match is promoted using the channel's own
 * dimensions, so it is shown once (not duplicated). When nothing matches, a
 * channel-default item is built from the reduced aspect ratio.
 */
export function getCanvaRatioMenuItems(defaultDimensions: {
  width: string
  height: string
}): CanvaRatioMenuItem[] {
  const reducedRatio = getReducedAspectRatio(
    defaultDimensions.width,
    defaultDimensions.height,
  )

  const matching =
    CanvaRatioOptions.find(
      (option) =>
        option.dimensions.width === defaultDimensions.width &&
        option.dimensions.height === defaultDimensions.height,
    ) ?? CanvaRatioOptions.find((option) => option.ratio === reducedRatio)

  const defaultItem: CanvaRatioMenuItem = matching
    ? {
        ...matching,
        // Honour the channel's own size when promoting a ratio-only match.
        dimensions: { ...defaultDimensions, units: 'px' },
        isDefault: true,
      }
    : {
        id: 'channel-default',
        label: '',
        ratio: reducedRatio,
        dimensions: { ...defaultDimensions, units: 'px' },
        isDefault: true,
      }

  const rest = CanvaRatioOptions.filter((option) => option !== matching).map(
    (option) => Object.assign({}, option, { isDefault: false }),
  )

  return [defaultItem, ...rest]
}

/**
 * Title line for a ratio menu item: the friendly name plus ratio
 * (ex. "Landscape 1.91:1"). An unnamed channel default shows the bare
 * aspect ratio (ex. "2:3").
 */
export function getCanvaRatioMenuItemTitle(item: CanvaRatioMenuItem): string {
  return item.label ? `${item.label} ${item.ratio}` : item.ratio
}

/**
 * Muted secondary label for a ratio menu item: the pixel dimensions in
 * parentheses (ex. "(1200×627)"), with a "· Default" marker appended for the
 * default item.
 */
export function getCanvaRatioMenuItemDescription(
  item: CanvaRatioMenuItem,
): string {
  const dimensions = `(${item.dimensions.width}×${item.dimensions.height})`

  return item.isDefault ? `${dimensions} · Default` : dimensions
}
